// POFormXL - Sprint 44 Fase 3 Vista 9.
// Form contenedor PO con header (estado, ref, proveedor, acciones PDF/Send/Save),
// 3 tabs (basicos, lineas, auditoria), sidebar resumen con total destacado.
import { useEffect } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';
import usePOFormXL from './hooks/usePOFormXL';
import POLineasXL from './POLineasXL';
import SupplierPickerXL from '../../components/shared/SupplierPickerXL';
import SendButtons from '../../components/shared/SendButtons';
import openProtectedFile from '../../api/openProtectedFile';
import XLBadge from '../../components/xl/XLBadge';
import XLChip from '../../components/xl/XLChip';
import XLInput from '../../components/xl/XLInput';
import XLSelect from '../../components/xl/XLSelect';
import XLSkeleton from '../../components/xl/XLSkeleton';
import { fmtMoney } from '../../utils/money';

const CSS_ID = 'xl-po-form-css';
const CSS = `
.xl-page { padding: 24px; font-family: var(--xl-font-sans); color: var(--xl-color-text) }
.xl-hdr { display: flex; align-items: flex-start; justify-content: space-between;
  gap: 16px; margin-bottom: 16px; flex-wrap: wrap; }
.xl-hdr__left { display: flex; align-items: center; gap: 14px; min-width: 0 }
.xl-hdr__back {
  font: 500 13px/1 var(--xl-font-sans);
  color: var(--xl-color-text-secondary);
  background: transparent; border: none; cursor: pointer;
  padding: 6px 8px; border-radius: var(--xl-radius-md);
}
.xl-hdr__back:hover { background: var(--xl-color-surface-alt); color: var(--xl-color-text) }
.xl-hdr__title { margin: 0; font-size: 20px; font-weight: 600; line-height: 1.2 }
.xl-hdr__sub { margin: 2px 0 0; font-size: 12px; color: var(--xl-color-text-secondary) }
.xl-hdr__sub b { color: var(--xl-color-text); font-family: var(--xl-font-mono) }
.xl-hdr__actions { display: flex; gap: 8px; align-items: center; flex-wrap: wrap }
.xl-btn {
  font: 500 13px/1 var(--xl-font-sans); padding: 10px 16px;
  border-radius: var(--xl-radius-md); border: 1px solid transparent; cursor: pointer;
}
.xl-btn:focus-visible { outline: 2px solid var(--xl-color-focus); outline-offset: 2px }
.xl-btn:disabled { opacity: 0.45; cursor: not-allowed }
.xl-btn--primary { background: var(--xl-color-primary); color: var(--xl-color-text-inverse) }
.xl-btn--primary:hover { background: var(--xl-color-primary-hover) }
.xl-btn--ghost {
  background: transparent; color: var(--xl-color-text-secondary);
  border-color: var(--xl-color-border);
}
.xl-btn--ghost:hover { background: var(--xl-color-surface); color: var(--xl-color-text) }

.xl-banner { padding: 10px 14px; margin-bottom: 12px; border-radius: var(--xl-radius-md); font-size: 13px }
.xl-banner--ok  { background: rgba(5,150,105,0.10); color: var(--xl-color-success); border: 1px solid rgba(5,150,105,0.28) }
.xl-banner--err { background: rgba(220,38,38,0.10); color: var(--xl-color-danger);  border: 1px solid rgba(220,38,38,0.28) }

.xl-card {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); box-shadow: var(--xl-shadow-sm);
  overflow: hidden;
}
.xl-tabs { display: flex; gap: 6px; flex-wrap: wrap; padding: 12px 16px; background: var(--xl-color-bg); border-bottom: 1px solid var(--xl-color-border) }
.xl-body { padding: 20px }
.xl-body__grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px }
@media (max-width: 720px) { .xl-body__grid { grid-template-columns: 1fr } }
.xl-body__full { grid-column: 1 / -1 }

.xl-resumen {
  background: var(--xl-color-surface); border: 1px solid var(--xl-color-border);
  border-radius: var(--xl-radius-lg); box-shadow: var(--xl-shadow-sm);
  padding: 16px; margin-bottom: 16px;
}
.xl-resumen__row { display: flex; align-items: center; justify-content: space-between; padding: 4px 0; font-size: 12px }
.xl-resumen__lbl { color: var(--xl-color-text-secondary) }
.xl-resumen__val { color: var(--xl-color-text); font-family: var(--xl-font-mono) }
.xl-resumen__total {
  margin-top: 12px; padding: 12px;
  background: rgba(37,99,235,0.10); border: 1px solid rgba(37,99,235,0.28);
  border-radius: var(--xl-radius-md);
}
.xl-resumen__total-lbl { font-size: 10px; text-transform: uppercase; color: var(--xl-color-info); font-weight: 700 }
.xl-resumen__total-val { font-family: var(--xl-font-mono); font-size: 16px; font-weight: 600; color: var(--xl-color-info); margin-top: 4px }
.xl-loading { display: flex; flex-direction: column; gap: 10px }
`;

const TABS = [
  { id: 'basicos',   label: 'Datos basicos' },
  { id: 'lineas',    label: 'Lineas' },
  { id: 'auditoria', label: 'Auditoria' },
];
const MONEDA_OPTIONS = ['USD', 'EUR', 'COP', 'CNY'].map((m) => ({ value: m, label: m }));
const ESTADO_OPTIONS = [
  { value: 'draft', label: 'Borrador' },
  { value: 'confirmed', label: 'Aprobada' },
  { value: 'recibido_parcial', label: 'Recibido parcial' },
  { value: 'recibido_total', label: 'Recibido total' },
  { value: 'cancelled', label: 'Cancelada' },
];
const ESTADO_VARIANT = {
  draft: 'neutral', confirmed: 'info', recibido_parcial: 'warning',
  recibido_total: 'success', cancelled: 'danger',
};
const ESTADO_LABEL = {
  draft: 'Borrador', confirmed: 'Aprobada',
  recibido_parcial: 'Recibido parcial',
  recibido_total: 'Recibido total',
  cancelled: 'Cancelada',
};

function fmtFecha(v) {
  if (!v) return '\u2014';
  try { return new Date(v).toLocaleString('es-CO'); } catch (_) { return String(v); }
}

export default function POFormXL() {
  useEffect(() => { ensureKeyframes(); injectCSS(CSS_ID, CSS); }, []);
  const fm = usePOFormXL();

  if (fm.loading) {
    return (
      <div className="xl-page">
        <div className="xl-loading" aria-busy="true">
          <XLSkeleton variant="line" width="40%" height={20} />
          <XLSkeleton variant="rect" height={120} />
          <XLSkeleton variant="rect" height={220} />
        </div>
      </div>
    );
  }

  const onPdf = async () => {
    if (!fm.isEdit) return;
    try {
      await openProtectedFile('/po/' + fm.poId + '/pdf', 'application/pdf');
    } catch (e) { alert('No se pudo abrir el PDF: ' + ((e && e.message) || 'error')); }
  };

  const titulo = fm.isEdit ? ('Editar PO #' + fm.poId) : 'Nueva PO';

  return (
    <div className="xl-page">
      <header className="xl-hdr">
        <div className="xl-hdr__left">
          <button type="button" className="xl-hdr__back"
            onClick={() => fm.navigate('/po')}>{'\u2190 Volver'}</button>
          <div>
            <h2 className="xl-hdr__title">{titulo}</h2>
            <p className="xl-hdr__sub">
              <XLBadge variant={ESTADO_VARIANT[fm.form.estado] || 'neutral'} dot>
                {ESTADO_LABEL[fm.form.estado] || fm.form.estado}
              </XLBadge>
              {fm.form.referencia && <> {' \u00b7 '} <b>{fm.form.referencia}</b></>}
              {fm.form.proveedor_nombre && <> {' \u00b7 '} {fm.form.proveedor_nombre}</>}
            </p>
          </div>
        </div>
        <div className="xl-hdr__actions">
          {fm.isEdit && (
            <>
              <button type="button" className="xl-btn xl-btn--ghost" onClick={onPdf}>PDF</button>
              <SendButtons endpointBase={'/po/' + fm.poId} tipoLabel="PO"
                defaultEmail={fm.form.proveedor_email}
                defaultTelefono={fm.form.proveedor_telefono} />
            </>
          )}
          <button type="button" className="xl-btn xl-btn--primary"
            onClick={fm.onSave} disabled={fm.saving}>
            {fm.saving ? 'Guardando\u2026' : (fm.isEdit ? 'Guardar' : 'Crear')}
          </button>
        </div>
      </header>

      {fm.msg && (
        <div className={'xl-banner ' + (fm.msg.kind === 'ok' ? 'xl-banner--ok' : 'xl-banner--err')}
          role={fm.msg.kind === 'ok' ? 'status' : 'alert'}
          onClick={fm.clearMsg}>
          {fm.msg.text}
        </div>
      )}

      <div className="xl-resumen">
        <div className="xl-resumen__row">
          <span className="xl-resumen__lbl">Referencia</span>
          <span className="xl-resumen__val">{fm.form.referencia || '\u2014'}</span>
        </div>
        <div className="xl-resumen__row">
          <span className="xl-resumen__lbl">Proveedor</span>
          <span className="xl-resumen__val" style={{ fontFamily: 'inherit' }}>
            {fm.form.proveedor_nombre || '\u2014'}
          </span>
        </div>
        <div className="xl-resumen__row">
          <span className="xl-resumen__lbl">Fecha</span>
          <span className="xl-resumen__val">{fm.form.fecha_po || '\u2014'}</span>
        </div>
        <div className="xl-resumen__total">
          <div className="xl-resumen__total-lbl">Total PO</div>
          <div className="xl-resumen__total-val">{fmtMoney(fm.form.total_po_moneda, fm.form.moneda)}</div>
          {fm.form.total_po_cop > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--xl-color-text-muted)', fontFamily: 'var(--xl-font-mono)', marginTop: '2px' }}>
              {fmtMoney(fm.form.total_po_cop, 'COP')}
            </div>
          )}
        </div>
      </div>

      <div className="xl-card">
        <div className="xl-tabs" role="tablist">
          {TABS.map((t) => (
            <XLChip key={t.id} onClick={() => fm.setTab(t.id)} selected={fm.tab === t.id}>
              {t.label}
            </XLChip>
          ))}
        </div>
        <div className="xl-body">
          {fm.tab === 'basicos' && (
            <div className="xl-body__grid">
              <XLInput label="Referencia" value={fm.form.referencia}
                onChange={(e) => fm.set('referencia', e.target.value)}
                placeholder="auto si se deja vacio" error={fm.errors.referencia} />
              <XLSelect label="Estado" value={fm.form.estado}
                onChange={(e) => fm.set('estado', e.target.value)}
                options={ESTADO_OPTIONS} />
              <div className="xl-body__full">
                <SupplierPickerXL
                  currentName={fm.form.proveedor_nombre}
                  currentSupplierId={fm.form.proveedor_id}
                  onSelectSupplier={(s) => {
                    fm.set('proveedor_id', s.id);
                    fm.set('proveedor_nombre', s.nombre);
                    if (s.moneda_principal) fm.set('moneda', s.moneda_principal);
                  }}
                  onClear={() => { fm.set('proveedor_id', null); fm.set('proveedor_nombre', ''); }}
                  required
                  error={fm.errors.proveedor_id} />
              </div>
              <XLInput label="Fecha PO" type="date" value={fm.form.fecha_po}
                onChange={(e) => fm.set('fecha_po', e.target.value)} />
              <XLSelect label="Moneda" value={fm.form.moneda}
                onChange={(e) => fm.set('moneda', e.target.value)}
                options={MONEDA_OPTIONS} />
              <div className="xl-body__full">
                <XLInput label="Descripcion" value={fm.form.descripcion}
                  onChange={(e) => fm.set('descripcion', e.target.value)} />
              </div>
            </div>
          )}
          {fm.tab === 'lineas' && (
            fm.isEdit
              ? <POLineasXL poId={fm.poId} moneda={fm.form.moneda} />
              : <p style={{ color: 'var(--xl-color-text-muted)', fontSize: '13px' }}>
                  Guarda primero para agregar lineas.
                </p>
          )}
          {fm.tab === 'auditoria' && (
            fm.isEdit ? (
              <div className="xl-body__grid">
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--xl-color-text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Creada</div>
                  <div className="xl-resumen__val">{fmtFecha(fm.form.created_at)}</div>
                </div>
                <div>
                  <div style={{ fontSize: '10px', color: 'var(--xl-color-text-muted)', textTransform: 'uppercase', marginBottom: '4px' }}>Actualizada</div>
                  <div className="xl-resumen__val">{fmtFecha(fm.form.updated_at)}</div>
                </div>
              </div>
            ) : <p style={{ color: 'var(--xl-color-text-muted)', fontSize: '13px' }}>Aparecera despues de guardar.</p>
          )}
        </div>
      </div>
    </div>
  );
}
